/**
 * Employee tracking mode — mirrors UsersTableComponent.getModeTooltip() /
 * getModeIcon() in users-table.component.ts. Hidden and Admin Controlled are
 * separate modes with different downgrade remediation flows.
 */
export type EmployeeTrackingMode =
  | 'hidden'
  | 'admin_controlled'
  | 'employee_controlled'
  | 'unknown';

export function isTruthyModeSetting(value: unknown): boolean {
  if (value === true || value === 1) return true;
  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized === 'true' || normalized === '1' || normalized === 'yes';
}

export function getEmployeeSettingsRecord(user: any): Record<string, unknown> {
  const settings = user?.meta?.settings;
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return {};
  }
  return settings as Record<string, unknown>;
}

export function getAgentLabel(user: any): string {
  return String(user?.appInfo?.label ?? '').trim().toUpperCase();
}

/**
 * Extracts and normalizes desktop agent major version from user.appInfo.version.
 * Examples: '5.1.13' -> 5, 'v-5.0.110' -> 5, '4.2.2' -> 4, 'unknown' -> null, null/undefined -> null.
 */
export function getAgentMajorVersion(user: any): number | null {
  const raw = String(user?.appInfo?.version ?? '')
    .replace(/^v-?/i, '')
    .trim();
  if (!raw || !/\d/.test(raw)) {
    return null;
  }
  const major = parseInt(raw.split('.')[0], 10);
  return Number.isNaN(major) ? null : major;
}

/**
 * Checks whether an employee has installed application version 5.x.x or above (Rust agent).
 */
export function isAgentVersion5OrAbove(user: any): boolean {
  const major = getAgentMajorVersion(user);
  return major !== null && major >= 5;
}

/**
 * Resolves effective silent mode setting:
 * - If user has personalized settings enabled, checks user's ss_silent / is_silent.
 * - If personalized settings are disabled or absent, checks workspace global ss_silent / is_silent.
 */
export function isSilentSettingEnabled(
  user: any,
  globalSettings: Record<string, unknown>
): boolean {
  const settings = getEmployeeSettingsRecord(user);
  const hasPersonalizedFlag = settings.ss_personalizeSettings !== undefined;
  const isPersonalizedActive = isTruthyModeSetting(settings.ss_personalizeSettings);

  let candidateSilent: unknown;

  if (hasPersonalizedFlag) {
    if (isPersonalizedActive) {
      candidateSilent = settings.ss_silent !== undefined ? settings.ss_silent : settings.is_silent;
    } else {
      candidateSilent = globalSettings?.ss_silent !== undefined ? globalSettings.ss_silent : globalSettings?.is_silent;
    }
  } else {
    if (settings.ss_silent !== undefined || settings.is_silent !== undefined) {
      candidateSilent = settings.ss_silent !== undefined ? settings.ss_silent : settings.is_silent;
    } else {
      candidateSilent = globalSettings?.ss_silent !== undefined ? globalSettings.ss_silent : globalSettings?.is_silent;
    }
  }

  return isTruthyModeSetting(candidateSilent);
}

function resolveAutoTrackingMode(
  user: any,
  settings: Record<string, unknown>,
  globalSettings: Record<string, unknown>
): EmployeeTrackingMode {
  const autoTracking = settings.ss_automatic_tracking !== undefined
    ? settings.ss_automatic_tracking
    : globalSettings?.ss_automatic_tracking;
  return isTruthyModeSetting(autoTracking)
    ? 'admin_controlled'
    : 'employee_controlled';
}

export function getEmployeeTrackingMode(
  user: any,
  globalSettings: Record<string, unknown>
): EmployeeTrackingMode {
  const majorVersion = getAgentMajorVersion(user);
  const settings = getEmployeeSettingsRecord(user);

  // 1. Version >= 5.x.x (Rust Agent: unified client, mode determined by settings)
  if (majorVersion !== null && majorVersion >= 5) {
    if (isSilentSettingEnabled(user, globalSettings)) {
      return 'hidden';
    }
    const label = getAgentLabel(user);
    if (label === 'R') {
      return 'admin_controlled';
    }
    return resolveAutoTrackingMode(user, settings, globalSettings);
  }

  // 2. Version < 5.x.x (Electron 4.x: dedicated installers for Silent vs General)
  if (majorVersion !== null && majorVersion < 5) {
    const label = getAgentLabel(user);
    if (label === 'S') {
      return 'hidden';
    }
    if (label === 'R') {
      return 'admin_controlled';
    }
    const versionType = getWpVersionType(user);
    if (versionType === 'silent') {
      return 'hidden';
    }
    if (versionType === 'restricted') {
      return 'admin_controlled';
    }
    return resolveAutoTrackingMode(user, settings, globalSettings);
  }

  // 3. User has NOT installed application (no appInfo or no version)
  // Check settings instead of Firebase
  if (isSilentSettingEnabled(user, globalSettings)) {
    return 'hidden';
  }
  const versionType = getWpVersionType(user);
  if (versionType === 'silent') {
    return 'hidden';
  }
  if (versionType === 'restricted') {
    return 'admin_controlled';
  }
  return resolveAutoTrackingMode(user, settings, globalSettings);
}

export function isHiddenTrackingMode(
  user: any,
  globalSettings: Record<string, unknown>
): boolean {
  return getEmployeeTrackingMode(user, globalSettings) === 'hidden';
}

export function isAdminControlledTrackingMode(
  user: any,
  globalSettings: Record<string, unknown>
): boolean {
  return getEmployeeTrackingMode(user, globalSettings) === 'admin_controlled';
}

export function collectHiddenModeDetails(
  user: any,
  globalSettings: Record<string, unknown>
): string[] {
  const majorVersion = getAgentMajorVersion(user);
  if (majorVersion !== null && majorVersion >= 5) {
    return ['Silent mode enabled in settings'];
  }
  if (majorVersion !== null && majorVersion < 5) {
    return ['Hidden agent installed'];
  }
  return ['Silent mode enabled in settings'];
}

export function collectAdminControlModeDetails(
  user: any,
  globalSettings: Record<string, unknown>
): string[] {
  const label = getAgentLabel(user);
  const settings = getEmployeeSettingsRecord(user);
  const versionType = getWpVersionType(user);

  if (label === 'R' || versionType === 'restricted') {
    return ['Admin Controlled agent installed'];
  }

  if (settings.ss_automatic_tracking !== undefined) {
    if (isTruthyModeSetting(settings.ss_automatic_tracking)) {
      return ['Auto Tracking (Restricted Mode) enabled'];
    }
  } else if (isTruthyModeSetting(globalSettings.ss_automatic_tracking)) {
    return ['Inheriting workspace Auto Tracking (Restricted Mode)'];
  }

  return ['Inheriting workspace Auto Tracking (Restricted Mode)'];
}

function getWpVersionType(user: any): string {
  const candidates = [user?.version_type, user?.meta?.version_type, user?.meta?.agent_version_type];
  for (const candidate of candidates) {
    const normalized = String(candidate ?? '').trim().toLowerCase();
    if (normalized === 'silent' || normalized === 'hidden' || normalized === 's') {
      return 'silent';
    }
    if (normalized === 'restricted' || normalized === 'r') {
      return 'restricted';
    }
    if (normalized === 'general' || normalized === 'g') {
      return 'general';
    }
  }
  return '';
}
